/**
 * Describes the shape of a value without the value itself, so logs can show
 * what a filter input looked like without leaking PHI.
 *
 * Never add the value, a substring, a first letter or a hash of it here.
 * Short values like MRNs and DOBs can be brute-forced from a hash.
 *
 * Mirrors graphql/src/util/describeValue.ts so FE and BFF logs use the same shape fields.
 */

export interface ValueShape {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'null' | 'undefined';
  length?: number;
  words?: number;
  hasComma?: boolean;
  hasApostrophe?: boolean;
  hasHyphen?: boolean;
  hasNonAscii?: boolean;
  hasLeadingOrTrailingSpace?: boolean;
  isAllUpperCase?: boolean;
  isDateOnly?: boolean;
  hasTime?: boolean;
  hasOffset?: boolean;
  items?: ValueShape[];
}

const MAX_DESCRIBED_ITEMS = 10;
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const OFFSET_REGEX = /(Z|[+-]\d{2}:?\d{2})$/;
// eslint-disable-next-line no-control-regex
const NON_ASCII_REGEX = /[^\x00-\x7F]/;

function describeString(value: string): ValueShape {
  const trimmed = value.trim();
  const shape: ValueShape = {
    type: 'string',
    length: value.length,
    words: trimmed === '' ? 0 : trimmed.split(/\s+/).length,
    hasComma: value.includes(','),
    hasApostrophe: /['’]/.test(value),
    hasHyphen: value.includes('-'),
    hasNonAscii: NON_ASCII_REGEX.test(value),
    hasLeadingOrTrailingSpace: value !== trimmed,
    isAllUpperCase: /[a-z]/i.test(value) && value === value.toUpperCase(),
  };

  if (DATE_ONLY_REGEX.test(trimmed)) return { ...shape, isDateOnly: true, hasTime: false };

  if (DATE_TIME_REGEX.test(trimmed)) {
    return { ...shape, isDateOnly: false, hasTime: true, hasOffset: OFFSET_REGEX.test(trimmed) };
  }

  return shape;
}

export function describeValue(value: unknown): ValueShape {
  if (value === null) return { type: 'null' };
  if (value === undefined) return { type: 'undefined' };
  if (Array.isArray(value)) {
    return {
      type: 'array',
      length: value.length,
      items: value.slice(0, MAX_DESCRIBED_ITEMS).map(describeValue),
    };
  }
  if (typeof value === 'string') return describeString(value);
  if (typeof value === 'number') return { type: 'number' };
  if (typeof value === 'boolean') return { type: 'boolean' };

  return { type: 'object' };
}
